<script setup>
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    value: { type: Array, default: () => [] },
    api: { type: Object },
    node: { type: Object },
    column: { type: Object },
    rowIndex: { type: Number },
    params: { type: Object }
});

//const emit = defineEmits(['download', 'delete', 'upload', 'cellValueChanged']);
const { onView, onCellValueChanged } = props.params;
const hasFile = ref(false);
const fileName = ref('');
const fileInput = ref(null);
const allowedFileTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/vnd.android.package-archive'];
const isFileRegistered = computed(() => {
    return props.params.value && Array.isArray(props.params.value) && props.params.value.length > 0 && props.params.value[0]?.attachment_id;
});
const isFileDeleted = ref(false);
const fileInfo = ref(null);

onMounted(() => {
    //console.log('Initial props:', props);
    init(props);
});

function init(params) {
    if (params?.params?.value) {
        updateFileStatus(params.params.value);
    }
}

function refresh(params) {
    if (!params || !params.value) {
        return false;
    }
    // 파일이 이미 있는 경우 refresh하지 않음
    if (hasFile.value && fileName.value) {
        return true;
    }
    updateFileStatus(params.value);
    return true;
}

function updateFileStatus(value) {
    fileInfo.value = value;

    if (Array.isArray(value) && value.length > 0) {
        hasFile.value = true;
        fileName.value = value[0].original_filename || '';
        isFileDeleted.value = false;
    } else if (typeof value === 'object' && value !== null) {
        if (value.upload) {
            hasFile.value = true;
            fileName.value = value.upload.name;
            isFileDeleted.value = false;
        } else if (value.delete) {
            hasFile.value = true;
            fileName.value = props.params.value[0].original_filename || ''; //value.delete;
            isFileDeleted.value = true;
        } else {
            hasFile.value = false;
            fileName.value = '';
            isFileDeleted.value = false;
        }
    } else {
        // 값이 없거나 예상치 못한 형식일 경우 기존 상태를 유지
        return;
    }

    //console.log('Updated state:', { hasFile: hasFile.value, fileName: fileName.value, isFileDeleted: isFileDeleted.value });
}

watch(
    () => props.params?.value,
    (newValue) => {
        //console.log('props.params.value changed:', newValue);
        updateFileStatus(newValue);
    },
    { deep: true }
);

function triggerCellValueChanged(fileInfo) {
    try {
        const colId = props.params.column.getColId();
        const rowNode = props.params.node;

        if (props.params.onCellValueChanged) {
            props.params.onCellValueChanged(fileInfo, {
                data: rowNode.data,
                node: rowNode,
                column: props.params.column,
                api: props.params.api
            });
        }

        rowNode.setDataValue(colId, fileInfo);
        updateFileStatus(fileInfo);
    } catch (error) {
        console.error('triggerCellValueChanged error:', error);
    }
    // 셀 리프레시
    // props.params.api.refreshCells({
    //     force: true,
    //     rowNodes: [rowNode],
    //     columns: [colId]
    // });
}

function handleUpload() {
    if (fileInput.value) {
        fileInput.value.value = '';
    }
    const code = props.params.node.data?.code;
    if (!code) {
        triggerCellValueChanged({
            delete: '',
            upload: ''
        });
        return;
    }

    fileInput.value.click();
}

function handleFileChange(event) {
    const file = event.target.files[0];
    if (file && allowedFileTypes.includes(file.type)) {
        const fileInfo = {
            delete: '',
            upload: file
        };
        onCellValueChanged(fileInfo);
        triggerCellValueChanged(fileInfo);
        updateFileStatus(fileInfo);

        // 파일 선택 후 input 값 초기화
        if (fileInput.value) {
            fileInput.value.value = '';
        }
    }
}

function handleDownload(event) {
    try {
        if (isFileRegistered.value && !isFileDeleted.value) {
            onView(props.params.value[0]);
            event.stopPropagation();
        }
    } catch (error) {
        console.error('Error handleDownload:', error);
    }
}

function handleDelete(event) {
    if (isFileRegistered.value) {
        isFileDeleted.value = true;
        // fileName은 그대로 유지
        const fileInfo = {
            delete: props.params.value[0].attachment_filename,
            upload: ''
        };
        onCellValueChanged(fileInfo);
        triggerCellValueChanged(fileInfo);
    } else {
        const fileInfo = {
            delete: '',
            upload: ''
        };
        onCellValueChanged(fileInfo);
        triggerCellValueChanged(fileInfo);
        hasFile.value = false;
        // fileName은 유지
        isFileDeleted.value = true;
    }
    if (fileInput.value) {
        fileInput.value.value = '';
    }
    event.stopPropagation();
}

function handleRestore(event) {
    if (isFileRegistered.value) {
        // 기존에 저장된 파일이 있는 경우
        isFileDeleted.value = false;
        const fileInfo = {
            delete: '',
            upload: props.params.value[0]
        };
        onCellValueChanged(fileInfo);
        triggerCellValueChanged(fileInfo);
        fileName.value = props.params.value[0].original_filename;
    } else {
        // 기존에 저장된 파일이 없는 경우
        const fileInfo = {
            delete: '',
            upload: ''
        };
        onCellValueChanged(fileInfo);
        triggerCellValueChanged(fileInfo);
        hasFile.value = false;
        fileName.value = '';
    }
    event.stopPropagation();
}

defineExpose({ init, refresh, destroy: () => {} });
</script>

<template>
    <div class="flex items-center justify-between" style="width: 100%; gap: 0.5rem">
        <span
            :class="{
                'cursor-pointer': isFileRegistered && !isFileDeleted,
                'cursor-default': !isFileRegistered || isFileDeleted
            }"
            :style="{
                fontSize: '0.875rem',
                fontFamily: 'SCoreDream400',
                maxWidth: 'calc(100% - 4rem)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: !fileName ? '#ccc' : isFileRegistered && isFileDeleted ? '#999' : 'inherit',
                textDecoration: isFileRegistered && isFileDeleted ? 'line-through' : 'none',
                textDecorationColor: 'red'
            }"
            @click="isFileRegistered && !isFileDeleted ? handleDownload($event) : null"
        >
            {{ fileName || '파일 없음' }}
            <!-- <i v-if="!fileName" class="pi pi-minus" style="font-size: 0.875rem"></i> -->
        </span>
        <div class="flex items-center" style="gap: 0.5rem">
            <i v-if="!hasFile || (!isFileRegistered && !isFileDeleted)" class="pi pi-upload cursor-pointer" @click="handleUpload" style="font-size: 1rem"></i>
            <template v-if="hasFile">
                <template v-if="isFileRegistered && !isFileDeleted">
                    <i class="pi pi-trash cursor-pointer text-red-600" @click="handleDelete" style="font-size: 1rem"></i>
                </template>
                <i v-else-if="isFileDeleted" class="pi pi-undo cursor-pointer text-blue-600" @click="handleRestore" style="font-size: 1rem"></i>
                <i v-else class="pi pi-trash cursor-pointer text-red-600" @click="handleDelete" style="font-size: 1rem"></i>
            </template>
        </div>
    </div>
    <input type="file" ref="fileInput" @change="handleFileChange" accept=".pdf,.jpeg,.jpg,.png,.apk" style="display: none" />
</template>
